import { Card, Container, Divider, Grid, Stack, Text } from '@mantine/core';
import { useLocalStorage } from '@mantine/hooks';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { AUTO_REFRESH_INTERVAL_MS, useOperations } from './api/queries';
import type { DateRange } from './api/types';
import { AppHeader } from './components/AppHeader';
import { BalanceSummary } from './components/BalanceSummary';
import { CategoryChart, currentMonthRange } from './components/CategoryChart';
import { OperationDetails } from './components/OperationDetails';
import { OperationsFilters } from './components/OperationsFilters';
import { OperationsTable } from './components/OperationsTable';
import { PageControls } from './components/PageControls';
import { SidebarPanels } from './components/SidebarPanels';
import { usePageState } from './lib/pageState';

const AUTO_REFRESH_STORAGE_KEY = 'ledger:auto-refresh';

export function App() {
  const {
    state: { kind, category, pageSize, page, selectedId },
    update,
    openOperation,
    closeOperation,
  } = usePageState();
  const [autoRefresh, setAutoRefresh] = useLocalStorage({
    key: AUTO_REFRESH_STORAGE_KEY,
    defaultValue: false,
  });
  const [range, setRange] = useState<DateRange>(currentMonthRange);

  const refreshInterval = autoRefresh ? AUTO_REFRESH_INTERVAL_MS : false;
  const params = useMemo(
    () => ({ kind, category, ...range, limit: pageSize, offset: page * pageSize }),
    [kind, category, range, pageSize, page],
  );
  const { data, isPending, error, refetch } = useOperations(params, refreshInterval);

  // The chart and table share their dates. With the filter on «Все», the chart
  // falls back to withdrawals, the side worth dividing up.
  const breakdownKind = kind ?? 'withdrawal';

  const queryClient = useQueryClient();
  const fetching = useIsFetching() > 0;

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <AppHeader
          refreshing={fetching}
          autoRefresh={autoRefresh}
          onRefresh={() => {
            void queryClient.invalidateQueries();
          }}
          onAutoRefreshChange={setAutoRefresh}
        />

        {/* Ledger on the left, what is planned on the right; stacked on narrow screens. */}
        <Grid gap="xl" align="flex-start">
          <Grid.Col span={{ base: 12, lg: 8 }}>
            <Stack gap="xl">
              <BalanceSummary range={range} refreshInterval={refreshInterval} />

              <CategoryChart
                kind={breakdownKind}
                range={range}
                selected={category}
                onSelect={(next) => update({ category: next, page: 0 })}
                onRangeChange={(next) => {
                  setRange(next);
                  update({ page: 0 });
                }}
                refreshInterval={refreshInterval}
              />

              <Card padding="md">
                <Stack gap="md">
                  <OperationsFilters
                    kind={kind}
                    category={category}
                    pageSize={pageSize}
                    onKindChange={(next) => update({ kind: next, page: 0 })}
                    onCategoryChange={(next) => update({ category: next, page: 0 })}
                    onPageSizeChange={(next) => update({ pageSize: next, page: 0 })}
                  />

                  <Divider />

                  <OperationsTable
                    operations={data?.operations ?? []}
                    loading={isPending}
                    error={error}
                    onRetry={() => {
                      void refetch();
                    }}
                    onSelect={openOperation}
                  />

                  {error === null && (
                    <PageControls
                      page={page}
                      pageSize={pageSize}
                      shown={data?.operations.length ?? 0}
                      hasMore={data?.hasMore ?? false}
                      onPageChange={(next) => update({ page: next })}
                    />
                  )}
                </Stack>
              </Card>
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, lg: 4 }}>
            <SidebarPanels refreshInterval={refreshInterval} />
          </Grid.Col>
        </Grid>

        <Text size="xs" c="dimmed" ta="center">
          Данные только для чтения. Записать операцию, добавить хотелку, завести обязательный
          платёж, разовый план, долг или поправить его график можно через MCP.
        </Text>
      </Stack>

      <OperationDetails operationId={selectedId} onClose={closeOperation} />
    </Container>
  );
}
